/**
 * State for the Netzplan import wizard.
 *
 * On `loadFromFile` it parses the Netzplan, pulls live customers/objects/services (via
 * the existing domain stores), matches, and seeds per-row decisions with sensible
 * defaults. The three steps mutate those decisions; `build*Rows` selectors turn the
 * confirmed decisions into the row arrays that `buildWorkbooks.ts` writes. Kept free of
 * any commit/transport concern — the CommitStep orchestrates the actual imports.
 */

import { create } from 'zustand';
import { parseNetzplan } from '@/pages/Import/logic/parseNetzplan';
import { matchCustomerName, matchObject, norm } from '@/pages/Import/logic/match';
import {
  ACT_SERVICE,
  inferSchedule,
  serviceForActivity,
  stripAddr,
  type ScheduleTyp,
} from '@/pages/Import/logic/mapping';
import type {
  CustomerRowOut,
  ObjectRowOut,
  RuleRowOut,
  TaskRowOut,
} from '@/pages/Import/logic/buildWorkbooks';
import type {
  DecisionAction,
  LiveCustomer,
  LiveObject,
  LiveService,
  MatchCategory,
  NetzplanActivity,
  StagingModel,
} from '@/pages/Import/logic/types';
import { useCustomerStore } from './useCustomerStore';
import { useObjectStore } from './useObjectStore';
import { useServiceStore } from './useServiceStore';

export type WizardStep = 'upload' | 'customers' | 'objects' | 'tasks' | 'commit';
export const WIZARD_STEPS: WizardStep[] = ['upload', 'customers', 'objects', 'tasks', 'commit'];

const DEFAULT_LAND = 'Österreich';
const DEFAULT_DURATION = 60;

/** A distinct customer to ensure exists — a regular header or a bucket child. */
export interface CustomerSource {
  id: string;
  rawName: string;
  isBucketChild: boolean;
  category: MatchCategory;
  score: number;
  suggestion: LiveCustomer | null;
}
export interface CustomerDecision {
  action: DecisionAction;
  linkedCustomerId: string;
  name: string;
  street: string;
  plz: string;
  ort: string;
  land: string;
}

export interface ObjectSource {
  key: string;
  customerSourceId: string;
  objName: string;
  category: MatchCategory;
  score: number;
  suggestion: LiveObject | null;
  isBucket: boolean;
  activities: NetzplanActivity[];
}
export interface ObjectDecision {
  action: DecisionAction;
  linkedObjectId: string;
  name: string;
  street: string;
  plz: string;
  ort: string;
  land: string;
}

export interface TaskSource {
  id: string;
  objectKey: string;
  activity: string;
  raws: string[];
  missingService: boolean;
  noteOnly: boolean;
}
export interface TaskDecision {
  action: 'create' | 'skip';
  service: string;
  typ: ScheduleTyp;
  von: string;
  bis: string;
  dauer: number;
}

interface NetzplanImportState {
  step: WizardStep;
  loading: boolean;
  error: string | null;
  fileName: string | null;
  staging: StagingModel | null;
  live: { customers: LiveCustomer[]; objects: LiveObject[]; services: LiveService[] };

  customerSources: CustomerSource[];
  customerDecisions: Record<string, CustomerDecision>;
  objectSources: ObjectSource[];
  objectDecisions: Record<string, ObjectDecision>;
  taskSources: TaskSource[];
  taskDecisions: Record<string, TaskDecision>;

  setStep: (step: WizardStep) => void;
  loadFromFile: (file: File) => Promise<void>;
  setCustomerDecision: (id: string, patch: Partial<CustomerDecision>) => void;
  setObjectDecision: (key: string, patch: Partial<ObjectDecision>) => void;
  setTaskDecision: (id: string, patch: Partial<TaskDecision>) => void;
  refreshLive: () => Promise<void>;
  reset: () => void;

  missingServiceNames: () => string[];
  buildCustomerRows: () => CustomerRowOut[];
  buildObjectRows: () => ObjectRowOut[];
  buildTaskRows: () => { tasks: TaskRowOut[]; rules: RuleRowOut[] };
}

function toStatus(): 'Aktiv' { return 'Aktiv'; }

async function loadLiveData(): Promise<NetzplanImportState['live']> {
  const customerStore = useCustomerStore.getState();
  const objectStore = useObjectStore.getState();
  const serviceStore = useServiceStore.getState();
  await Promise.all([
    customerStore.fetchAllCustomers(),
    objectStore.fetchAllObjects(),
    serviceStore.fetchServices(),
  ]);
  const customers: LiveCustomer[] = useCustomerStore.getState().customers.map((c) => ({
    id: c.id ?? '',
    name: c.name ?? '',
    customerNumber: c.customerNumber ?? '',
  }));
  const objects: LiveObject[] = useObjectStore.getState().objects.map((o) => ({
    id: (o.id as string) ?? '',
    customerId: (o.customerId as string) ?? '',
    name: (o.name as string) ?? '',
    street: ((o.address as { street?: string } | undefined)?.street) ?? '',
  }));
  const services: LiveService[] = useServiceStore.getState().services.map((s) => ({
    id: s.id ?? '',
    name: s.name ?? '',
  }));
  return { customers, objects, services };
}

const EMPTY_LIVE = { customers: [], objects: [], services: [] };

export const useNetzplanImportStore = create<NetzplanImportState>((set, get) => ({
  step: 'upload',
  loading: false,
  error: null,
  fileName: null,
  staging: null,
  live: EMPTY_LIVE,
  customerSources: [],
  customerDecisions: {},
  objectSources: [],
  objectDecisions: {},
  taskSources: [],
  taskDecisions: {},

  setStep: (step) => set({ step }),

  reset: () =>
    set({
      step: 'upload', loading: false, error: null, fileName: null, staging: null, live: EMPTY_LIVE,
      customerSources: [], customerDecisions: {}, objectSources: [], objectDecisions: {},
      taskSources: [], taskDecisions: {},
    }),

  refreshLive: async () => {
    const live = await loadLiveData();
    set({ live });
  },

  loadFromFile: async (file) => {
    set({ loading: true, error: null, fileName: file.name });
    try {
      const [staging, live] = await Promise.all([parseNetzplan(file), loadLiveData()]);

      // --- Customer sources: regular headers + bucket children ---
      const customerSources: CustomerSource[] = [];
      const customerDecisions: Record<string, CustomerDecision> = {};

      for (const c of staging.customers) {
        if (c.isBucket) continue; // handled per child below
        const m = matchCustomerName(c.rawNames[0] ?? c.key, live.customers);
        const src: CustomerSource = {
          id: c.key, rawName: c.rawNames[0] ?? c.key, isBucketChild: false,
          category: m.category, score: m.score, suggestion: m.suggestion,
        };
        customerSources.push(src);
        customerDecisions[src.id] = defaultCustomerDecision(src);
      }

      const bucketChildCustomerId: Record<string, string> = {}; // objectKey -> customer source id
      for (const o of staging.objects) {
        if (!o.isBucket) continue;
        const cleaned = stripAddr(o.objName);
        const m = matchCustomerName(cleaned, live.customers);
        const id = `b:${o.key}`;
        bucketChildCustomerId[o.key] = id;
        const src: CustomerSource = {
          id, rawName: cleaned, isBucketChild: true, category: m.category, score: m.score, suggestion: m.suggestion,
        };
        customerSources.push(src);
        customerDecisions[id] = defaultCustomerDecision(src);
      }

      // --- Object sources ---
      const liveByCust = new Map<string, LiveObject[]>();
      for (const o of live.objects) {
        const arr = liveByCust.get(o.customerId) ?? [];
        arr.push(o);
        liveByCust.set(o.customerId, arr);
      }
      const custMatchByKey = new Map<string, ReturnType<typeof matchCustomerName>>();
      for (const c of staging.customers) {
        if (c.isBucket) continue;
        custMatchByKey.set(c.key, matchCustomerName(c.rawNames[0] ?? c.key, live.customers));
      }

      const objectSources: ObjectSource[] = [];
      const objectDecisions: Record<string, ObjectDecision> = {};
      for (const o of staging.objects) {
        const customerSourceId = o.isBucket ? bucketChildCustomerId[o.key] : o.custKey;
        const custMatch = o.isBucket ? null : custMatchByKey.get(o.custKey) ?? null;
        const liveForCust = custMatch?.suggestion ? (liveByCust.get(custMatch.suggestion.id) ?? []) : [];
        const m = matchObject(o, liveForCust);
        const src: ObjectSource = {
          key: o.key, customerSourceId, objName: o.objName, category: o.isBucket ? 'NEU' : m.category,
          score: m.score, suggestion: o.isBucket ? null : m.suggestion, isBucket: o.isBucket, activities: o.activities,
        };
        objectSources.push(src);
        objectDecisions[o.key] = defaultObjectDecision(src);
      }

      // --- Task sources (object × activity, excluding pure notes) ---
      const taskSources: TaskSource[] = [];
      const taskDecisions: Record<string, TaskDecision> = {};
      for (const o of staging.objects) {
        const isOffice = o.custKey === 'bueroreinigung';
        for (const act of o.activities) {
          if (act.activity === '_Notiz') continue;
          const cfg = ACT_SERVICE[act.activity];
          const id = `${o.key}::${act.activity}`;
          const src: TaskSource = {
            id, objectKey: o.key, activity: act.activity, raws: act.raws,
            missingService: !!cfg?.missing, noteOnly: !!cfg?.noteOnly,
          };
          taskSources.push(src);
          const sched = inferSchedule(act.months);
          taskDecisions[id] = {
            action: cfg?.noteOnly ? 'skip' : 'create',
            service: serviceForActivity(act.activity, isOffice),
            typ: sched.typ, von: sched.von, bis: sched.bis, dauer: DEFAULT_DURATION,
          };
        }
      }

      set({
        staging, live, customerSources, customerDecisions, objectSources, objectDecisions,
        taskSources, taskDecisions, loading: false, step: 'customers',
      });
    } catch (e) {
      set({ loading: false, error: e instanceof Error ? e.message : 'Import fehlgeschlagen' });
    }
  },

  setCustomerDecision: (id, patch) =>
    set((s) => ({ customerDecisions: { ...s.customerDecisions, [id]: { ...s.customerDecisions[id], ...patch } } })),
  setObjectDecision: (key, patch) =>
    set((s) => ({ objectDecisions: { ...s.objectDecisions, [key]: { ...s.objectDecisions[key], ...patch } } })),
  setTaskDecision: (id, patch) =>
    set((s) => ({ taskDecisions: { ...s.taskDecisions, [id]: { ...s.taskDecisions[id], ...patch } } })),

  missingServiceNames: () => {
    const { taskSources, taskDecisions, live } = get();
    return computeMissingServices(taskSources, taskDecisions, live.services);
  },

  buildCustomerRows: () => {
    const { customerSources, customerDecisions } = get();
    const rows: CustomerRowOut[] = [];
    for (const src of customerSources) {
      const d = customerDecisions[src.id];
      if (!d || d.action !== 'create') continue;
      rows.push({ name: d.name, street: d.street, plz: d.plz, ort: d.ort, land: d.land || DEFAULT_LAND, status: toStatus() });
    }
    return rows;
  },

  buildObjectRows: () => {
    const { objectSources, objectDecisions, customerDecisions, live } = get();
    const rows: ObjectRowOut[] = [];
    for (const src of objectSources) {
      const d = objectDecisions[src.key];
      if (!d || d.action !== 'create') continue;
      const cust = customerDecisions[src.customerSourceId];
      // Object import resolves the customer by number or by unique name, so a not-yet-committed
      // new customer can be referenced by its name here.
      const kunde = customerRef(cust, live, true);
      if (!kunde) continue; // customer skipped → object cannot be attached
      rows.push({ kunde, objektname: d.name, street: d.street, plz: d.plz, ort: d.ort, land: d.land || DEFAULT_LAND, status: toStatus() });
    }
    return rows;
  },

  buildTaskRows: () => {
    const { taskSources, taskDecisions, objectSources, objectDecisions, customerDecisions, live } = get();
    const numById = new Map(live.customers.map((c) => [c.id, c.customerNumber]));
    const objByKey = new Map(objectSources.map((o) => [o.key, o]));

    // Build the "K-… — Objektname" label an object resolves to (existing or freshly created).
    // Tasks commit last, after customers + objects are committed and live is refreshed, so a
    // created customer now resolves to its assigned number.
    const objectLabel = (objectKey: string): string | null => {
      const o = objByKey.get(objectKey);
      if (!o) return null;
      const od = objectDecisions[objectKey];
      if (!od || od.action === 'skip') return null;
      if (od.action === 'link' && od.linkedObjectId) {
        const lo = live.objects.find((x) => x.id === od.linkedObjectId);
        const cn = lo ? numById.get(lo.customerId) ?? '' : '';
        return lo ? `${cn} — ${lo.name}` : null;
      }
      const cust = customerDecisions[o.customerSourceId];
      const num = customerRef(cust, live, false); // number only (not name) for the task label
      return num ? `${num} — ${od.name || o.objName}` : null;
    };

    const tasks: TaskRowOut[] = [];
    const rules: RuleRowOut[] = [];
    const seenTask = new Set<string>();
    for (const src of taskSources) {
      const d = taskDecisions[src.id];
      if (!d || d.action === 'skip') continue;
      const label = objectLabel(src.objectKey);
      if (!label) continue;
      const taskKey = `${label}::${d.service}`;
      if (!seenTask.has(taskKey)) {
        seenTask.add(taskKey);
        tasks.push({ objekt: label, dienstleistung: d.service, dauer: d.dauer, aktiv: 'Ja' });
      }
      rules.push({
        aufgabe: `${label} · ${d.service}`,
        typ: d.typ,
        von: d.typ === 'Saisonal' ? d.von : '',
        bis: d.typ === 'Saisonal' ? d.bis : '',
        aktiv: 'Ja',
      });
    }
    return { tasks, rules };
  },
}));

/** Pure: service names referenced by non-skipped tasks that have no live counterpart. */
export function computeMissingServices(
  taskSources: TaskSource[],
  taskDecisions: Record<string, TaskDecision>,
  liveServices: LiveService[],
): string[] {
  const existing = new Set(liveServices.map((s) => norm(s.name)));
  const needed = new Set<string>();
  for (const src of taskSources) {
    const d = taskDecisions[src.id];
    if (!d || d.action === 'skip') continue;
    if (!existing.has(norm(d.service))) needed.add(d.service);
  }
  return [...needed];
}

function defaultCustomerDecision(src: CustomerSource): CustomerDecision {
  const link = src.category !== 'NEU' && !!src.suggestion;
  return {
    action: link ? 'link' : 'create',
    linkedCustomerId: src.suggestion?.id ?? '',
    name: src.rawName,
    street: '', plz: '', ort: '', land: DEFAULT_LAND,
  };
}

function defaultObjectDecision(src: ObjectSource): ObjectDecision {
  const link = src.category === 'MATCH' && !!src.suggestion;
  return {
    action: link ? 'link' : 'create',
    linkedObjectId: src.suggestion?.id ?? '',
    name: src.objName,
    street: src.objName, plz: '', ort: '', land: DEFAULT_LAND,
  };
}

/**
 * Resolve the customer reference for an object/task cell.
 * - linked customer → its number.
 * - created customer → its number once it exists in refreshed live data; otherwise, when
 *   `allowName` is set (object import accepts a plain unique name), fall back to the name.
 */
function customerRef(
  cust: CustomerDecision | undefined,
  live: NetzplanImportState['live'],
  allowName: boolean,
): string | null {
  if (!cust || cust.action === 'skip') return null;
  if (cust.action === 'link') {
    const c = live.customers.find((x) => x.id === cust.linkedCustomerId);
    return c ? c.customerNumber : null;
  }
  const created = live.customers.find((x) => norm(x.name) === norm(cust.name));
  if (created) return created.customerNumber;
  return allowName ? cust.name || null : null;
}
